/**
 * Complete logic for the suggestions for Intelligent Bookseller
 */
import { addToCart } from "./add-to-cart";
import { formatMoney } from "./g-utils";
import { ButtonLoader } from "./intelligent-bookseller-button-loader.js";
import { findBestCombination } from "./intelligent-bookseller-combinations.js";

/**
 * Display the content of the suggestions
 * @param {Object} apiResponse - The API response
 * @param {Object} elements - The DOM elements necessary
 * @param {HTMLElement} elements.suggestionsEmpty - Element for the empty state
 * @param {HTMLElement} elements.suggestionsDescription - Element for the description
 * @param {HTMLElement} elements.suggestionsDescriptionText - Text of the description
 * @param {HTMLElement} elements.suggestionsItems - Container of the items
 * @param {HTMLElement} elements.suggestionsAddAllBtn - Button "Add all"
 * @param {Function} populateSuggestionsItemsFromSection - Function to populate the items
 * @param {Function} showEmptySuggestions - Function to show the empty state
 * @returns {Promise<boolean>} true if success, false otherwise
 */
export async function setSuggestionsContent(apiResponse, elements, populateSuggestionsItemsFromSection, showEmptySuggestions) {
  // Validate apiResponse
  if (!apiResponse || apiResponse.status !== 'success') {
    console.error('Invalid API response:', apiResponse);
    showEmptySuggestions(elements);
    return false;
  }

  const summary = apiResponse.magic_cart_suggestions?.summary || null;
  const suggestions = apiResponse.magic_cart_suggestions?.suggestions || null;
  const allSuggestions = apiResponse.magic_cart_suggestions?.allSuggestions || null;

  if (suggestions && allSuggestions) {
    // Hide empty suggestions
    if (elements.suggestionsEmpty) {
      elements.suggestionsEmpty.setAttribute('aria-hidden', 'true');
    }

    // Show suggestions description
    if (elements.suggestionsDescription && elements.suggestionsDescriptionText) {
      if (summary && summary.trim() !== '') {
        elements.suggestionsDescriptionText.textContent = summary;
        elements.suggestionsDescription.removeAttribute('aria-hidden');
      } else {
        elements.suggestionsDescription.setAttribute('aria-hidden', 'true');
        elements.suggestionsDescriptionText.textContent = '';
      }
    }

    // Validate allSuggestions array
    if (!Array.isArray(allSuggestions) || allSuggestions.length === 0) {
      console.warn('No allSuggestions in suggestions:', allSuggestions);
      showEmptySuggestions(elements);
      return false;
    }

    // Validate suggestions array
    if (!Array.isArray(suggestions) || suggestions.length === 0) {
      console.warn('No suggestions in suggestions:', suggestions);
      showEmptySuggestions(elements);
      return false;
    }

    // Populate and show suggestions items
    try {
      await populateSuggestionsItemsFromSection(allSuggestions, suggestions);
      if (elements.suggestionsItems) {
        elements.suggestionsItems.removeAttribute('aria-hidden');
      }

      // Enable add all suggestions button
      if (elements.suggestionsAddAllBtn) {
        elements.suggestionsAddAllBtn.removeAttribute('aria-disabled');
      }
    } catch (error) {
      console.error('Error populating suggestions items:', error);
      // Show empty state on error
      showEmptySuggestions(elements);
      return false;
    }
  } else {
    // Show empty suggestions
    showEmptySuggestions(elements);
  }

  return true;
}

/**
 * Show the empty state of the suggestions
 * @param {Object} elements - The DOM elements necessary
 */
export function showEmptySuggestions(elements) {
  // Show empty suggestions
  if (elements.suggestionsEmpty) {
    elements.suggestionsEmpty.removeAttribute('aria-hidden');
  }

  // Hide suggestions description
  if (elements.suggestionsDescription) {
    elements.suggestionsDescription.setAttribute('aria-hidden', 'true');
  }
  if (elements.suggestionsDescriptionText) {
    elements.suggestionsDescriptionText.textContent = '';
  }

  // Hide suggestions items
  if (elements.suggestionsItems) {
    elements.suggestionsItems.setAttribute('aria-hidden', 'true');
  }

  // Disable add all suggestions button
  if (elements.suggestionsAddAllBtn) {
    elements.suggestionsAddAllBtn.setAttribute('aria-disabled', 'true');
  }
}

/**
 * Populate the items of the suggestions from a Shopify section
 * @param {Array} products - All the products available
 * @param {Array} selectedProducts - The selected products to display
 * @param {HTMLElement} container - The container to inject the HTML
 * @returns {Promise<void>}
 */
export async function populateSuggestionsItemsFromSection(products, selectedProducts, container) {
  // Validate products array
  if (!Array.isArray(products) || products.length === 0) {
    throw new Error('Products array is empty or invalid');
  }

  // Validate selectedProducts array
  if (!Array.isArray(selectedProducts) || selectedProducts.length === 0) {
    throw new Error('Selected products array is empty or invalid');
  }

  const sectionId = 'intelligent-bookseller-suggestions-items';
  
  try {
    const productIds = products.map(product => product.id).join(',');
    const selectedProductIds = selectedProducts.map(product => product.id).join(',');
    const url = `${window.Shopify.routes.root}?sections=${sectionId}&product_ids=${productIds}&selected_ids=${selectedProductIds}`;
    
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Failed to fetch section: ${response.status}`);
    }

    const json = await response.json();
    const sectionHtml = json[sectionId];

    if (!sectionHtml) {
      throw new Error('Section HTML not found in response');
    }

    // Extract content from .js-content div if it exists, otherwise use full HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = sectionHtml;
    const contentDiv = tempDiv.querySelector('.js-content');
    const htmlToInject = contentDiv ? contentDiv.innerHTML : sectionHtml;

    // Validate that we got some HTML
    if (!htmlToInject || htmlToInject.trim() === '') {
      throw new Error('Section returned empty HTML - no products found');
    }

    // Inject HTML into DOM
    if (container) {
      container.innerHTML = htmlToInject;
    } else {
      throw new Error('Suggestions items container not found');
    }
  } catch (error) {
    console.error('Error populating suggestions items from section:', error);
    throw error;
  }
}

/**
 * Remove an item from the suggestions
 * @param {string} productHandle - The handle of the product to remove
 * @param {Object} state - The state object
 * @param {Object} elements - The DOM elements necessary
 * @param {HTMLElement} elements.suggestionsItems - Container of the items
 * @param {Function} showEmptySuggestions - Function to show the empty state
 * @param {Function} saveState - Function to save the state
 */
export function removeItem(productHandle, state, elements, showEmptySuggestions, saveState) {
  // Validate API response
  if (!state.apiResponse || !state.apiResponse.magic_cart_suggestions) {
    console.error('No API response available');
    return;
  }

  const { suggestions, allSuggestions } = state.apiResponse.magic_cart_suggestions;

  if (!suggestions || !allSuggestions || !Array.isArray(suggestions) || !Array.isArray(allSuggestions)) {
    console.error('Invalid suggestions data');
    return;
  }

  // Find item to remove
  const itemToRemove = suggestions.find(item => item.id === productHandle);
  if (!itemToRemove) {
    console.warn(`Item with handle ${productHandle} not found in suggestions`);
    return;
  }

  // Find item element to remove
  const itemToRemoveElement = elements.suggestionsItems?.querySelector(
    `intelligent-bookseller-suggestion-item[data-product-handle="${productHandle}"]`
  );
  if (!itemToRemoveElement) {
    console.warn(`Item element with handle ${productHandle} not found in DOM`);
  }

  // Remove from suggestions and allSuggestions
  const newSuggestions = suggestions.filter(item => item.id !== productHandle);
  state.apiResponse.magic_cart_suggestions.suggestions = newSuggestions;
  const newAllSuggestions = allSuggestions.filter(item => item.id !== productHandle);
  state.apiResponse.magic_cart_suggestions.allSuggestions = newAllSuggestions;

  // Hide item in DOM
  if (itemToRemoveElement) {
    itemToRemoveElement.setAttribute('aria-hidden', 'true');
  }

  // Show empty state if no items left
  if (newSuggestions.length === 0) {
    showEmptySuggestions(elements);
  }

  saveState();

  // Intelligent Bookseller: data layer event
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'remove_book_from_selection'
  });
}

/**
 * Replace an item by another in the suggestions
 * @param {string} productHandle - The handle of the product to replace
 * @param {Object} state - The state object
 * @param {Object} elements - The DOM elements necessary
 * @param {HTMLElement} elements.suggestionsItems - Container of the items
 * @param {Function} saveState - Function to save the state
 */
export function switchItem(productHandle, state, elements, saveState) {
  // Validate API response
  if (!state.apiResponse || !state.apiResponse.magic_cart_suggestions) {
    console.error('No API response available');
    throw new Error('No API response available');
  }

  const { suggestions, allSuggestions } = state.apiResponse.magic_cart_suggestions;
  const gap = state.apiResponse.cart_info?.gap || 0;

  if (!suggestions || !allSuggestions || !Array.isArray(suggestions) || !Array.isArray(allSuggestions)) {
    console.error('Invalid suggestions data');
    throw new Error('Invalid suggestions data');
  }

  // Find item to replace
  const itemToReplace = suggestions.find(item => item.id === productHandle);
  if (!itemToReplace) {
    console.error(`Item with handle ${productHandle} not found in suggestions`);
    throw new Error(`Item not found in suggestions`);
  }

  // Find item element to replace
  const itemToReplaceElement = elements.suggestionsItems?.querySelector(
    `intelligent-bookseller-suggestion-item[data-product-handle="${productHandle}"]`
  );
  if (!itemToReplaceElement) {
    console.error(`Item element with handle ${productHandle} not found in DOM`);
    throw new Error(`Item element not found in DOM`);
  }

  // Calculate target gap (gap minus other displayed items)
  const otherDisplayedItems = suggestions.filter(item => item.id !== productHandle);
  const otherItemsTotal = otherDisplayedItems.reduce((sum, item) => sum + (item.price || 0), 0);
  const gapTarget = gap - otherItemsTotal;

  // Find available replacement items
  const displayedIds = new Set(suggestions.map(item => item.id));
  const availableItems = allSuggestions.filter(item => !displayedIds.has(item.id));
  
  if (availableItems.length === 0) {
    console.warn('No available items to replace with');
    throw new Error('No available items to replace with');
  }

  // Calculate gap remaining for each candidate
  const candidates = availableItems.map(item => {
    const gapRemaining = gapTarget - (item.price || 0);
    return {
      item,
      gapRemaining,
      score: item.score || 0
    };
  });

  // Select best candidate (prefer negative gap closest to 0, or minimize positive gap)
  let bestCandidate = null;
  const negativeGapCandidates = candidates.filter(c => c.gapRemaining < 0);
  if (negativeGapCandidates.length > 0) {
    negativeGapCandidates.sort((a, b) => {
      const gapDiff = b.gapRemaining - a.gapRemaining;
      if (Math.abs(gapDiff) < 0.20) {
        return b.score - a.score;
      }
      return gapDiff;
    });
    bestCandidate = negativeGapCandidates[0];
  } else {
    candidates.sort((a, b) => {
      const gapDiff = a.gapRemaining - b.gapRemaining;
      if (Math.abs(gapDiff) < 0.20) {
        return b.score - a.score;
      }
      return gapDiff;
    });
    bestCandidate = candidates[0];
  }

  if (!bestCandidate) {
    console.error('No suitable replacement item found');
    throw new Error('No suitable replacement item found');
  }

  // Replace item in DOM
  const replacementItem = bestCandidate.item;
  const replacementElement = elements.suggestionsItems?.querySelector(
    `intelligent-bookseller-suggestion-item[data-product-handle="${replacementItem.id}"]`
  );
  
  if (!replacementElement) {
    console.error(`Replacement item element with handle ${replacementItem.id} not found in DOM`);
    throw new Error(`Replacement item element not found in DOM`);
  }

  // Clone element to reinitialize custom elements
  const clonedReplacementElement = replacementElement.cloneNode(true);
  
  // Insert clone at the position of itemToReplaceElement, then remove the old element
  // This ensures proper DOM positioning and prevents duplicate elements
  if (itemToReplaceElement.parentNode) {
    itemToReplaceElement.parentNode.insertBefore(clonedReplacementElement, itemToReplaceElement);
    itemToReplaceElement.remove();
  } else {
    // Fallback to replaceWith if parentNode is not available
    itemToReplaceElement.replaceWith(clonedReplacementElement);
  }
  
  // Make clone visible
  clonedReplacementElement.removeAttribute('aria-hidden');
  
  // Remove original replacement element (if it's still in the DOM)
  if (replacementElement.parentNode) {
    replacementElement.remove();
  }

  // Update state
  const newSuggestions = suggestions.map(item => 
    item.id === productHandle ? replacementItem : item
  );
  state.apiResponse.magic_cart_suggestions.suggestions = newSuggestions;
  
  const newAllSuggestions = allSuggestions.filter(item => item.id !== itemToReplace.id);
  state.apiResponse.magic_cart_suggestions.allSuggestions = newAllSuggestions;
  
  saveState();

  // Intelligent Bookseller: data layer event
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'change_book_selection'
  });
}

/**
 * Replace all visible items in the DOM
 * @param {Array} oldItems - The old items
 * @param {Array} newItems - The new items
 * @param {HTMLElement} container - The container of the items
 */
export function replaceAllItems(oldItems, newItems, container) {
  if (oldItems.length !== newItems.length) {
    console.error('Cannot replace items: count mismatch');
    throw new Error('Item count mismatch');
  }

  // Replace each item in DOM
  for (let i = 0; i < oldItems.length; i++) {
    const oldItem = oldItems[i];
    const newItem = newItems[i];

    // Skip if the item is the same (no replacement needed)
    if (oldItem.id === newItem.id) {
      continue;
    }

    // Find old item element in DOM (only visible ones)
    const oldItemElement = container?.querySelector(
      `intelligent-bookseller-suggestion-item[data-product-handle="${oldItem.id}"]:not([aria-hidden="true"])`
    );

    if (!oldItemElement) {
      console.warn(`Old item element with handle ${oldItem.id} not found in DOM or already hidden`);
      continue;
    }

    // Find new item element in DOM (it might be hidden)
    const newItemElement = container?.querySelector(
      `intelligent-bookseller-suggestion-item[data-product-handle="${newItem.id}"]`
    );

    if (!newItemElement) {
      console.error(`New item element with handle ${newItem.id} not found in DOM`);
      continue;
    }

    // If newItemElement is the same as oldItemElement (shouldn't happen but safety check)
    if (oldItemElement === newItemElement) {
      console.warn(`Old and new item elements are the same for handle ${oldItem.id}`);
      continue;
    }

    // Hide the new item element first if it's currently visible (to avoid duplication)
    if (!newItemElement.hasAttribute('aria-hidden') || newItemElement.getAttribute('aria-hidden') !== 'true') {
      newItemElement.setAttribute('aria-hidden', 'true');
    }

    // Clone new element to reinitialize custom elements
    const clonedNewElement = newItemElement.cloneNode(true);

    // Replace old element with cloned new element
    if (oldItemElement.parentNode) {
      oldItemElement.parentNode.insertBefore(clonedNewElement, oldItemElement);
      oldItemElement.remove();
    } else {
      oldItemElement.replaceWith(clonedNewElement);
    }

    // Make clone visible
    clonedNewElement.removeAttribute('aria-hidden');

    // Remove original new item element (if it's still in the DOM)
    if (newItemElement.parentNode) {
      newItemElement.remove();
    }
  }
}

/**
 * Refresh all the suggestions
 * @param {Object} state - The state object
 * @param {Object} elements - The DOM elements necessary
 * @param {Function} callbacks - The callbacks necessary
 * @param {Function} callbacks.buildPayload - Function to build the payload
 * @param {Function} callbacks.fetchMagicCartSuggestions - Function to call the API
 * @param {Function} callbacks.setSuggestionsContent - Function to display the suggestions
 * @param {Function} callbacks.saveState - Function to save the state
 * @returns {Promise<void>}
 */
export async function refreshSuggestions(state, elements, callbacks) {
  // Validate API response
  if (!state.apiResponse || !state.apiResponse.magic_cart_suggestions) {
    console.error('No API response available');
    throw new Error('No API response available');
  }

  const { suggestions, allSuggestions } = state.apiResponse.magic_cart_suggestions;
  const gap = state.apiResponse.cart_info?.gap || 0;

  if (!suggestions || !allSuggestions || !Array.isArray(suggestions) || !Array.isArray(allSuggestions)) {
    console.error('Invalid suggestions data');
    throw new Error('Invalid suggestions data');
  }

  // Calculate number of items currently displayed
  const itemsCount = suggestions.length;

  // Find all available items (not currently displayed)
  const availableItems = allSuggestions.filter(item => !suggestions.some(s => s.id === item.id));

  // If not enough items available, fetch new suggestions from API
  if (availableItems.length < itemsCount) {
    await refreshSuggestionsFromAPI(state, callbacks);
    return;
  }

  // Find best combination
  const bestCombination = findBestCombination(availableItems, itemsCount, gap);

  if (!bestCombination || bestCombination.length === 0) {
    console.warn('No suitable combination found, fetching new suggestions from API');
    await refreshSuggestionsFromAPI(state, callbacks);
    return;
  }

  // Replace all items in DOM and state
  replaceAllItems(suggestions, bestCombination, elements.suggestionsItems);

  // Update state
  state.apiResponse.magic_cart_suggestions.suggestions = bestCombination;
  
  // Update allSuggestions: remove old items
  const oldItemIds = new Set(suggestions.map(item => item.id));
  const newAllSuggestions = allSuggestions
    .filter(item => !oldItemIds.has(item.id));
  state.apiResponse.magic_cart_suggestions.allSuggestions = newAllSuggestions;

  callbacks.saveState();

  // Intelligent Bookseller: data layer event
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'change_total_selection'
  });
}

/**
 * Refresh the suggestions from the API
 * @param {Object} state - The state object
 * @param {Function} callbacks - The callbacks necessary
 * @returns {Promise<void>}
 */
async function refreshSuggestionsFromAPI(state, callbacks) {
  try {
    // Rebuild payload
    const payload = await callbacks.buildPayload();

    // Fetch new suggestions from API
    const apiResponse = await callbacks.fetchMagicCartSuggestions(payload);

    // Update state
    state.apiResponse = apiResponse;

    // Update suggestions content
    await callbacks.setSuggestionsContent(apiResponse);

    // Save state
    callbacks.saveState();
  } catch (error) {
    console.error('Error refreshing suggestions from API:', error);
    throw error;
  }
}

/**
 * Initialize the button "Edit the quiz"
 * @param {HTMLElement} button - The button
 * @param {Object} state - The state object
 * @param {Function} callbacks - The callbacks necessary
 * @param {Function} callbacks.restoreQuizData - Function to restore the quiz data
 * @param {Function} callbacks.setContent - Function to change the display
 * @returns {Function|null} The handler created
 */
export function initSuggestionsEditQuizButton(button, state, callbacks) {
  if (!button) return null;

  const handler = (e) => {
    e.preventDefault();
    e.stopPropagation();

    state.quizSubmitted = false;

    // Restore questionnaire data
    if (state.questionnaire) {
      callbacks.restoreQuizData(state.questionnaire);
    }

    // Update content to show quiz
    callbacks.setContent();
  };
  
  button.addEventListener('click', handler);
  return handler;
}

/**
 * Initialize the button "Refresh the suggestions"
 * @param {HTMLElement} button - The button
 * @param {Object} elements - The DOM elements necessary
 * @param {HTMLElement} elements.suggestionsRefreshBtnText - Text of the button
 * @param {HTMLElement} elements.suggestionsRefreshBtnLoader - Loader of the button
 * @param {HTMLElement} elements.suggestionsRefreshErrorMsg - Error message
 * @param {Object} state - The state object
 * @param {Function} callbacks - The callbacks necessary
 * @param {Function} callbacks.refreshSuggestions - Function to refresh the suggestions
 * @returns {Function|null} The handler created
 */
export function initSuggestionsRefreshButton(button, elements, state, callbacks) {
  if (!button) return null;

  const handler = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    // Hide error message
    if (elements.suggestionsRefreshErrorMsg) {
      elements.suggestionsRefreshErrorMsg.setAttribute('aria-hidden', 'true');
    }

    // Start loading
    ButtonLoader.setLoading(
      button,
      elements.suggestionsRefreshBtnText,
      elements.suggestionsRefreshBtnLoader,
      true
    );

    try {
      await callbacks.refreshSuggestions();
    } catch (error) {
      console.error('Error refreshing suggestions:', error);
      // Show error message
      if (elements.suggestionsRefreshErrorMsg) {
        elements.suggestionsRefreshErrorMsg.removeAttribute('aria-hidden');
      }
    } finally {
      // Stop loading
      ButtonLoader.setLoading(
        button,
        elements.suggestionsRefreshBtnText,
        elements.suggestionsRefreshBtnLoader,
        false
      );
    }
  };
  
  button.addEventListener('click', handler);
  return handler;
}

/**
 * Initialize the button "Add all to cart"
 * @param {HTMLElement} button - The button
 * @param {Object} elements - The DOM elements necessary
 * @param {HTMLElement} elements.suggestionsItems - Container of the items
 * @param {HTMLElement} elements.suggestionsAddAllBtnText - Text of the button
 * @param {HTMLElement} elements.suggestionsAddAllBtnLoader - Loader of the button
 * @param {HTMLElement} elements.suggestionsAddAllErrorMsg - Error message
 * @param {HTMLElement} elements.suggestionsAddAllSuccessMsg - Success message
 * @returns {Function|null} The handler created
 */
export function initSuggestionsAddAllButton(button, elements) {
  if (!button) return null;

  const handler = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    // Hide error and success messages at the start
    if (elements.suggestionsAddAllErrorMsg) {
      elements.suggestionsAddAllErrorMsg.setAttribute('aria-hidden', 'true');
    }
    if (elements.suggestionsAddAllSuccessMsg) {
      elements.suggestionsAddAllSuccessMsg.setAttribute('aria-hidden', 'true');
    }

    try {
      // Manage loading state
      ButtonLoader.setLoading(
        button,
        elements.suggestionsAddAllBtnText,
        elements.suggestionsAddAllBtnLoader,
        true
      );

      // Collect all available items
      const suggestionItems = elements.suggestionsItems?.querySelectorAll('intelligent-bookseller-suggestion-item:not([aria-hidden="true"])') || [];
      
      if (suggestionItems.length === 0) {
        console.warn('No suggestion items found');
        return;
      }

      const itemsToAdd = [];
      const trackingData = [];

      // Loop through all items and collect data
      for (const itemElement of suggestionItems) {
        const productForm = itemElement.querySelector('.js-product-form');
        if (!productForm) continue;

        // Get selected variant
        const variantIdInput = productForm['variant-id'];
        if (!variantIdInput) continue;

        const variantId = variantIdInput.value;
        if (!variantId) continue;

        // Get productJson to check availability
        const productJsonScript = itemElement.querySelector('.js-product-json');
        if (!productJsonScript) continue;

        const productJson = JSON.parse(productJsonScript.innerHTML);
        const selectedVariant = productJson.variants[variantId];
        
        if (!selectedVariant) continue;

        // Check availability
        const isAvailable = (selectedVariant.inventory_quantity > 0 || !selectedVariant.inventory_management) || (selectedVariant.sku && selectedVariant.sku.includes('NW'));

        if (!isAvailable) continue;

        // Get properties
        const properties = {
          Auteur: productForm['properties[Auteur]']?.value || '',
          _collectionTitle: productForm['properties[_collectionTitle]']?.value || '',
          _collectionId: productForm['properties[_collectionId]']?.value || '',
          _from_intelligent_bookseller: true,
          _addedFrom: 'magic_cart',
        };

        // Build item for API
        itemsToAdd.push({
          id: variantId,
          quantity: 1,
          properties: properties
        });

        // Store data for tracking
        const atcEventDataScript = itemElement.querySelector('.js-atc-event-data');
        const klaviyoAtcEventDataScript = itemElement.querySelector('.js-klaviyo-atc-event-data');
        
        if (atcEventDataScript && klaviyoAtcEventDataScript) {
          const initialAtcData = JSON.parse(atcEventDataScript.innerHTML);
          const initialKlaviyoData = JSON.parse(klaviyoAtcEventDataScript.innerHTML);

          // Prepare tracking data (will be completed after add)
          trackingData.push({
            productJson: productJson,
            selectedVariant: selectedVariant,
            initialAtcData: initialAtcData,
            initialKlaviyoData: initialKlaviyoData
          });
        }
      }

      if (itemsToAdd.length === 0) {
        console.warn('No available items to add to cart');
        return;
      }

      const eventData = [];
      const klaviyoItems = [];

      trackingData.forEach((tracking) => {
        const { selectedVariant, initialAtcData, initialKlaviyoData } = tracking;

        const atcEventData = { ...initialAtcData };
        atcEventData.cloud_retail.visitorId = localStorage.getItem('visitorId');
        atcEventData.cloud_retail.productDetails = [
          {
            product: {
              id: selectedVariant.barcode || selectedVariant.id,
            },
            quantity: 1
          },
        ];
        atcEventData.cloud_retail.cartId = window.cartToken;
        atcEventData.cloud_retail.userInfo = { userId: window.userId };
        if (atcEventData.productDetails && atcEventData.productDetails[0]) {
          atcEventData.productDetails[0].product.id = selectedVariant.barcode || selectedVariant.id;
        }
        eventData.push(atcEventData);

        const klaviyoAtcEventData = { ...initialKlaviyoData };
        klaviyoAtcEventData.Price = formatMoney(selectedVariant.price);
        klaviyoItems.push(klaviyoAtcEventData);
      });

      const json = await addToCart(itemsToAdd, { klaviyoItems, eventData });

      if (json) {
        if (elements.suggestionsAddAllSuccessMsg) {
          elements.suggestionsAddAllSuccessMsg.removeAttribute('aria-hidden');
        }
      } else {
        throw new Error('Failed to add items to cart');
      }
    } catch (error) {
      console.error('Error adding all suggestions to cart:', error);
      if (elements.suggestionsAddAllErrorMsg) {
        elements.suggestionsAddAllErrorMsg.removeAttribute('aria-hidden');
      }
    } finally {
      ButtonLoader.setLoading(
        button,
        elements.suggestionsAddAllBtnText,
        elements.suggestionsAddAllBtnLoader,
        false
      );
    }
  };
  
  button.addEventListener('click', handler);
  return handler;
}
